import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { FixedSizeList } from 'react-window';
import { useStatus } from '../context/StatusContext';
import { useDataUpdates } from '../hooks/useDataUpdates';
import { useDataCache } from '../hooks/useDataCache';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../styles/FastUpdates.css';
import '../styles/loading.css';
import LoadingAnimation from '../components/loadingAnimation';
import '../index.css';
import { StatusEvaluator } from '../context/StatusEvaluator';
import { getApiBase } from '../utils/apiBase';

const displayNames = {
    'UYS': 'UYS',
    'ENV': 'ENVISION',
    'flare': 'FLARE',
    'songbird': 'SONGBIRD'
  };

const VirtualizedGrid = React.memo(({ rowData, columnDefs }) => {
  const Row = ({ index, style }) => {
    const item = rowData[index];
    return (
      <div style={style} className="grid-row">
        <div className="feed-name">{item.feed_name}</div>
        <div className="price">{item.fast_update}</div>
        <div className="age" title={item.timestamp}>{item.time_since_last_update}</div>
      </div>
    );
  };

  return (
    <FixedSizeList
      height={400}
      itemCount={rowData.length}
      itemSize={34}
      width="100%"
      className="virtual-grid"
    >
      {Row}
    </FixedSizeList>
  );
});

const UpdateBox = React.memo(({ entity, network, data, columnDefs, defaultColDef, gridOptions, getHeaderStatus, getHeaderIcon }) => {
  if (!data || !data[entity] || !data[entity][network]) return null;

  const rowData = data[entity][network].map(item => ({
    ...item,
    timestamp: `Last Update: ${new Date(item.timestamp).toLocaleString()}`
  }));

  // Use server-computed age_secs for threshold evaluation.
  // Falls back to parsing the time_since_last_update string.
  const maxAge = Math.max(...rowData.map(item => {
    if (item.age_secs != null) return item.age_secs;
    const s = String(item.time_since_last_update || '');
    const match = s.match(/([\d.]+)s/);
    return match ? parseFloat(match[1]) : 0;
  }));

  const status = maxAge < 15 ? 'green' : maxAge < 30 ? 'amber' : 'red';

  return (
    <div className="update-box">
      <div className={`box-header ${getHeaderStatus(status)}`}>
        <div className="header-content">
          <h2>{displayNames[entity]} {displayNames[network]}</h2>
          <img 
            src={getHeaderIcon(status)}
            alt="Status Icon" 
            className="header-icon"
          />
        </div>
      </div>
      <div className="fast-updates-grid ag-theme-alpine-dark">
        {rowData.length > 100 ? (
          <VirtualizedGrid rowData={rowData} columnDefs={columnDefs} />
        ) : (
          <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
          />
        )}
      </div>
    </div>
  );
});


function FastUpdates() {
  const [fastUpdatesData, setFastUpdatesData] = useState({});
  const [loading, setLoading] = useState(true);
  const fastUpdatesDataRef = useRef(fastUpdatesData); // Ref to hold current data
  const { updatePageStatus } = useStatus();
  const { getCachedData, setCachedData } = useDataCache();
  const updatesGridRef = useRef(null);
  const scrollPosition = useRef(0);
  const debounceTimeout = useRef(null);

  // Pagination state
  const [activeSlide, setActiveSlide] = useState(0);
  const [numSlides, setNumSlides] = useState(0);

  const columnDefs = useMemo(() => [
    { 
      field: 'feed_name', 
      headerName: 'FEED',
      flex: 1.5,
      cellClass: 'feed-name'
    },
    { 
      field: 'fast_update', 
      headerName: 'PRICE',
      flex: 1,
      cellClass: 'price',
      type: 'numericColumn',
      headerClass: 'align-left'
    },
    { 
      field: 'time_since_last_update', 
      headerName: 'AGE',
      flex: 0.8,
      cellClass: 'age',
      tooltipField: 'timestamp'
    }
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
}), []);

  const gridOptions = useMemo(() => ({
    headerHeight: 48,
    rowHeight: 34,
    getRowClass: () => 'grid-row',
    suppressRowHoverHighlight: false,
    rowStyle: { 
      display: 'flex',
      alignItems: 'center'
    },
    tooltipShowDelay: 0,
    tooltipHideDelay: 2000
  }), []);

  const getHeaderStatus = (status) => {
    switch (status) {
        case 'green':
            return 'bg-green';
          case 'amber':
            return 'bg-amber';
          case 'red':
          default:
            return 'bg-red';
    }
  };
  
  const getHeaderIcon = (status) => {
    return status === 'green'
      ? "https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671760d711a4e1bbe2b0489d_tick-circle%20(1).svg"
      : "https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671760d7912d60d1ebb42595_info-circle%20(1).svg";
  };

  useEffect(() => {
    fastUpdatesDataRef.current = fastUpdatesData;
  }, [fastUpdatesData]);

  // Debounced fetch with scroll restoration (existing logic)
  const fetchData = useCallback(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
  
    debounceTimeout.current = setTimeout(async () => {
      // Preserve scroll position from AG-Grid if applicable, though this ref is now for the outer scroller
      // This part of scroll restoration might need re-evaluation if it was for internal grid scroll.
      // For now, we'll assume it might still be relevant or harmless.
      // const updatesGridAgInstance = updatesGridRef.current?.api; 
      // if (updatesGridAgInstance) { 
      //   scrollPosition.current = updatesGridAgInstance.getVerticalPixelRange().top;
      // }

      const cachedData = getCachedData('fast_updates');
      if (cachedData) {
        setFastUpdatesData(cachedData);
        setLoading(false);
        // Initial scroll to activeSlide for mobile might be needed here or in useLayoutEffect
        return;
      }
  
      try {
        const API = getApiBase();
        const response = await fetch(`${API}/fast_updates.json`, { cache: 'no-store' });
        const data = await response.json();
        
        // Compare with the value from the ref
        if (JSON.stringify(data) !== JSON.stringify(fastUpdatesDataRef.current)) { 
          console.log('Fast updates data changed, updating state');
          setFastUpdatesData(data);
          setCachedData('fast_updates', data);
          
          const status = StatusEvaluator.fastUpdates(data);
          updatePageStatus('fastUpdates', status);
        }
        
        setLoading(false);
  
        // Restore scroll position if applicable
        // if (updatesGridRef.current?.api) { 
        //   updatesGridRef.current.api.ensureIndexVisible(scrollPosition.current, 'top');
        // }
      } catch (error) {
        console.error('Error fetching fast updates data:', error);
        setLoading(false);
        updatePageStatus('fastUpdates', 'red');
      }
    }, 500);
  }, [updatePageStatus, getCachedData, setCachedData]); // Removed fastUpdatesData from dependencies

  // Use the shared SSE hook
  useDataUpdates(fetchData);

  // Initial fetch
  useEffect(() => {
    fetchData();
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [fetchData]);

  // Calculate number of slides based on available data
  useEffect(() => {
    if (fastUpdatesData) {
      // Count actual slides that will be rendered
      let count = 0;
      
      // Check each entity-network combination that will create a slide
      if (fastUpdatesData.UYS?.flare) count++;
      if (fastUpdatesData.UYS?.songbird) count++;
      if (fastUpdatesData.ENV?.flare) count++;
      if (fastUpdatesData.ENV?.songbird) count++;
      
      setNumSlides(count);
    }
  }, [fastUpdatesData]);

  // Scroll handler for pagination dots - completely rewritten
  const handleScroll = useCallback(() => {
    if (updatesGridRef.current && numSlides > 0) {
      const scroller = updatesGridRef.current;
      const scrollLeft = scroller.scrollLeft;
      
      // Get the width of the first visible slide (if available)
      const slideElements = scroller.querySelectorAll('.update-box');
      if (slideElements.length === 0) return;
      
      const slideWidth = slideElements[0].offsetWidth;
      const gap = 10; // From CSS
      const totalSlideWidth = slideWidth + gap;
      
      if (totalSlideWidth > 0) {
        // Calculate which slide is most visible (using same approach as Indexers.js)
        const currentSlide = Math.round(scrollLeft / totalSlideWidth);
        setActiveSlide(Math.max(0, Math.min(currentSlide, numSlides - 1)));
      }
    }
  }, [numSlides]);

  // Effect to add/remove scroll listener
  useLayoutEffect(() => {
    const scroller = updatesGridRef.current;
    if (scroller && numSlides > 0) {
      scroller.addEventListener('scroll', handleScroll);
      // Initial call to set correct dot
      handleScroll();
      return () => scroller.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, numSlides]);

  // Effect to add/remove scroll listener
  useLayoutEffect(() => {
    const scroller = updatesGridRef.current;
    if (scroller && numSlides > 0) {
      // Set initial scroll position explicitly to 0 to center first card
      scroller.scrollLeft = 0;
      
      // Add scroll event listener
      scroller.addEventListener('scroll', handleScroll);
      
      // Initial call to set correct dot
      handleScroll();
      
      return () => scroller.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, numSlides]);

  const renderedUpdateBoxes = useMemo(() => {
    return [
      <UpdateBox 
        key="UYS-flare" 
        entity="UYS" 
        network="flare" 
        data={fastUpdatesData} 
        columnDefs={columnDefs} 
        defaultColDef={defaultColDef} 
        gridOptions={gridOptions} 
        getHeaderStatus={getHeaderStatus} 
        getHeaderIcon={getHeaderIcon} 
      />,
      <UpdateBox 
        key="UYS-songbird" 
        entity="UYS" 
        network="songbird" 
        data={fastUpdatesData} 
        columnDefs={columnDefs} 
        defaultColDef={defaultColDef} 
        gridOptions={gridOptions} 
        getHeaderStatus={getHeaderStatus} 
        getHeaderIcon={getHeaderIcon} 
      />,
      <UpdateBox 
        key="ENV-flare" 
        entity="ENV" 
        network="flare" 
        data={fastUpdatesData} 
        columnDefs={columnDefs} 
        defaultColDef={defaultColDef} 
        gridOptions={gridOptions} 
        getHeaderStatus={getHeaderStatus} 
        getHeaderIcon={getHeaderIcon} 
      />,
      <UpdateBox 
        key="ENV-songbird" 
        entity="ENV" 
        network="songbird" 
        data={fastUpdatesData} 
        columnDefs={columnDefs} 
        defaultColDef={defaultColDef} 
        gridOptions={gridOptions} 
        getHeaderStatus={getHeaderStatus} 
        getHeaderIcon={getHeaderIcon} 
      />
    ];
  }, [fastUpdatesData, columnDefs, defaultColDef, gridOptions]);

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <div className="fast-updates-container">
      <div className="updates-grid" ref={updatesGridRef}>
        {renderedUpdateBoxes}
      </div>
      {/* Pagination Dots */} 
      {numSlides > 1 && (
        <div className="pagination-dots-container mobile-only-pagination">
          {Array.from({ length: numSlides }).map((_, index) => (
            <div
              key={index}
              className={`dot ${index === activeSlide ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default FastUpdates;